import * as React from "react";
import { styled } from "@mui/material/styles";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";

import Heading from "../../../component-lib/Atoms/heading/EventEHRHeading";
import {
  formatReportDateTime,
  formatFacilityChange,
} from "../ReportConstants";

const BootstrapDialog = styled(Dialog)(({ theme }) => ({
  "& .MuiDialogContent-root": {
    padding: theme.spacing(2),
  },
  "& .MuiPaper-root": {
    width: "620px",
    maxWidth: "none",
  },
}));

type TrailStep = Record<string, any>;

interface PatientFacilityTrailDialogProps {
  open: boolean;
  trail: Record<string, any> | null;
  handleClose: () => void;
}

const PatientFacilityTrailDialog = ({
  open = false,
  trail,
  handleClose,
}: PatientFacilityTrailDialogProps) => {
  const timeline: TrailStep[] = trail?.timeline || [];

  return (
    <BootstrapDialog open={open} onClose={handleClose}>
      <div className="flex items-center justify-between px-4 pt-3">
        <Heading title="Facility Movement Trail" />
        <IconButton aria-label="close" onClick={handleClose} size="small">
          <CloseIcon />
        </IconButton>
      </div>

      <DialogContent dividers>
        {/* Patient summary */}
        <div className="mb-4">
          <p className="text-neutral-90 font-semibold text-[15px]">
            {trail?.patientName || "-"}
          </p>
          <p className="text-neutral-60 text-[13px]">
            BSA ID: {trail?.bsaId || "-"}
            {trail?.currentFacilityName ? (
              <>
                {"  •  "}Currently at:{" "}
                <span className="text-neutral-90">
                  {trail.currentFacilityName}
                </span>
              </>
            ) : null}
          </p>
        </div>

        {/* Vertical timeline (oldest -> newest) */}
        {timeline.length === 0 ? (
          <p className="text-neutral-60 text-[13px] py-6 text-center">
            No facility movements have been recorded for this patient yet.
          </p>
        ) : (
          <ol className="relative border-l border-neutral-30 ml-2">
            {timeline.map((step, index) => (
              <li key={step?.id || index} className="mb-5 ml-4">
                <span className="absolute w-3 h-3 bg-primary-60 rounded-full -left-[6.5px] mt-1.5 border border-white" />
                <div className="flex items-center flex-wrap gap-1">
                  <p className="text-neutral-90 font-medium text-[14px]">
                    {step?.fromFacilityName ? (
                      <>
                        {step.fromFacilityName}
                        <span className="text-neutral-60"> → </span>
                        {step?.toFacilityName || "-"}
                      </>
                    ) : (
                      step?.toFacilityName || "-"
                    )}
                  </p>
                </div>
                <p className="text-neutral-60 text-[12px] mt-0.5">
                  {formatFacilityChange(step?.reason || step?.changeType)}
                </p>
                <p className="text-neutral-60 text-[12px] mt-0.5">
                  {formatReportDateTime(step?.changedAt)}
                  {step?.changedByName ? `  •  by ${step.changedByName}` : ""}
                </p>
              </li>
            ))}
          </ol>
        )}
      </DialogContent>
    </BootstrapDialog>
  );
};

export default PatientFacilityTrailDialog;
